import { Injectable, OnDestroy } from '@angular/core';
// SA-8637: Sentry loaded from CDN (see src/index.html + src/types/sentry-cdn.d.ts).
// Types imported explicitly because `Sentry.Scope` as a TS type requires the
// namespace form which the `declare const Sentry: typeof import(...)`
// declaration alone doesn't provide.
import type { Scope as SentryScope } from '@sentry/browser';

export type MicrofrontendErrorReportingDetails =
  | {
      event: 'captureException';
      error: unknown;
      context?: {
        tags?: Record<string, string>;
        extra?: Record<string, any>;
      };
    }
  | {
      event: 'captureMessage';
      message: string;
      context?: {
        tags?: Record<string, string>;
        extra?: Record<string, any>;
      };
    }
  | {
      event: 'captureLog';
      level: 'info' | 'warn' | 'error';
      message: string;
      context?: {
        tags?: Record<string, string>;
        extra?: Record<string, any>;
      };
    };

@Injectable({
  providedIn: 'root'
})
export class MicrofrontendErrorReportingService implements OnDestroy {
  constructor() {
    this.setupEventListener();
  }

  private setupEventListener(): void {
    window.addEventListener(
      'microfrontendErrorReporting',
      this.handleMicrofrontendErrorReporting as EventListener
    );
  }

  private handleMicrofrontendErrorReporting = (event: CustomEvent): void => {
    // SA-8637: defensive guard — see sentry.utils.ts. Sentry comes from the CDN
    // script in index.html; if that didn't load, drop the event silently rather
    // than crashing on `new Sentry.Scope()`.
    if (typeof Sentry === 'undefined') {
      return;
    }

    const payload = event.detail as MicrofrontendErrorReportingDetails;

    switch (payload.event) {
      case 'captureException':
        if (Sentry.isInitialized()) {
          Sentry.captureException(
            payload.error,
            this.buildScope(payload.context)
          );
        } else {
          console.error(payload.error, this.buildScope(payload.context));
        }
        break;
      case 'captureMessage':
        if (Sentry.isInitialized()) {
          Sentry.captureMessage(
            payload.message,
            this.buildScope(payload.context)
          );
        } else {
          console.log(payload.message, this.buildScope(payload.context));
        }
        break;
      case 'captureLog':
        // Sentry Logs (v10) takes a flat attributes object — tags and extras
        // collapse into the same record. Requires `enableLogs: true` on init.
        if (Sentry.isInitialized()) {
          Sentry.logger[payload.level](
            payload.message,
            this.flattenLogAttributes(payload.context)
          );
        } else {
          console[payload.level === 'info' ? 'log' : payload.level](
            payload.message,
            this.flattenLogAttributes(payload.context)
          );
        }
        break;
    }
  };

  private buildScope(context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
  }): SentryScope {
    const scope: SentryScope = new Sentry.Scope();
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    if (context?.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    return scope;
  }

  private flattenLogAttributes(context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
  }): Record<string, unknown> {
    return { ...(context?.tags ?? {}), ...(context?.extra ?? {}) };
  }

  ngOnDestroy(): void {
    window.removeEventListener(
      'microfrontendErrorReporting',
      this.handleMicrofrontendErrorReporting as EventListener
    );
  }
}
